import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../../shared/helpers/icons';
import { ActivatedRoute, Router } from '@angular/router';
import { StaffInfoComponent } from "./staff-info/staff-info.component";
import { ThreatKnowledgeComponent } from "./threat-knowledge/threat-knowledge.component";
import { BehaviorComponent } from "./behavior/behavior.component";
import { AcademicsComponent } from "./academics/academics.component";
import { TeacherPeerRelationshipComponent } from './teacher-peer-relationship/teacher-peer-relationship.component';
import { DepressionComponent } from './depression/depression.component';
import { AggressionComponent } from './aggression/aggression.component';
import { ParentContactComponent } from './parent-contact/parent-contact.component';
import { SummaryComponent } from './summary/summary.component';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { TeacherStaffInterviewService } from '../../../../shared/services/teacherStaffInterview.service';
import { ITeacherStaffInterview } from '../../../../shared/models/teacherStaffInterview';
import { TeacherStaffInterviewSubmissionComponent } from "./teacher-staff-interview-submission/teacher-staff-interview-submission.component";
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'teacher-staff-interview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    StaffInfoComponent,
    ThreatKnowledgeComponent,
    BehaviorComponent,
    AcademicsComponent,
    TeacherPeerRelationshipComponent,
    DepressionComponent,
    AggressionComponent,
    ParentContactComponent,
    SummaryComponent,
    TeacherStaffInterviewSubmissionComponent,
    PopupMessageComponent,
    SpinnerComponent
],
  templateUrl: './teacher-staff-interview.component.html',
  styleUrl: './teacher-staff-interview.component.css'
})
export class TeacherStaffInterviewComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentTeacherStaffInterview: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'staff_info_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'knowledge_of_threat_section_completion',
      nextStepDependency: 'staff_info_section_completion'
    },
    {
      id: 3,
      completionProperty: 'academic_section_completion',
      nextStepDependency: 'knowledge_of_threat_section_completion'
    },
    {
      id: 4,
      completionProperty: 'behavior_section_completion',
      nextStepDependency: 'academic_section_completion'
    },
    {
      id: 5,
      completionProperty: 'peer_relationships_section_completion',
      nextStepDependency: 'behavior_section_completion'
    },
    {
      id: 6,
      completionProperty: 'depression_section_completion',
      nextStepDependency: 'peer_relationships_section_completion'
    },
    {
      id: 7,
      completionProperty: 'aggression_section_completion',
      nextStepDependency: 'depression_section_completion'
    },
    {
      id: 8,
      completionProperty: 'parent_contact_section_completion',
      nextStepDependency: 'aggression_section_completion'
    },
    {
      id: 9,
      completionProperty: 'summary_section_completion',
      nextStepDependency: 'parent_contact_section_completion'
    }
  ];
  finalStepConfig = {
      id: 10,
      completionProperty: 'teacher_staff_interview_completion',
      nextStepDependency: 'summary_section_completion'
  };
  currentStep: number = 1;
  tooltips = ['Staff Info', 'Threat Knowledge', 'Academics', 'Behavior', 'Peer Relationship', 'Depression', 'Aggression', 'Parent Contact', 'Summary', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private teacherStaffInterviewService: TeacherStaffInterviewService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadTeacherStaffInterview();
  }

  loadTeacherStaffInterview(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.teacherStaffInterviewService.getTeacherStaffInterviewByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (response) {
            this.currentTeacherStaffInterview = response;
          } else {
            this.currentTeacherStaffInterview = null;
          }
          this.loading = false;
          return
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;  
        }
      });
    }
  }

  handleUpdateTeacherStaffInterview(data: ITeacherStaffInterview, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.teacherStaffInterviewService.updateTeacherStaffInterview(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadTeacherStaffInterview();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 10 ) {
            this.router.navigate(['/assessments/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating teach/staff interview:', error.message);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/assessments/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentTeacherStaffInterview) {
      return this.currentTeacherStaffInterview?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
